// NX table (shadcn Table restyled): 13 px cells padded 12/14, sticky muted header, hairline rows, clickable and group rows.
import type * as React from "react";

import { cn } from "@/lib/cn";

function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<"table"> & { containerClassName?: string }) {
  return (
    <div data-slot="table-container" className={cn("relative w-full overflow-x-auto", containerClassName)}>
      <table
        data-slot="table"
        className={cn("w-full border-collapse text-ui tabular-nums", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return <thead data-slot="table-header" className={cn(className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody data-slot="table-body" className={cn("[&_tr:last-child>td]:border-b-0", className)} {...props} />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot data-slot="table-footer" className={cn("border-t bg-subtle font-medium", className)} {...props} />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "transition-colors duration-120 data-[clickable=true]:cursor-pointer data-[clickable=true]:hover:bg-accent data-[state=selected]:bg-selected",
        className,
      )}
      {...props}
    />
  );
}

function TableGroupRow({
  className,
  children,
  colSpan,
  ...props
}: React.ComponentProps<"tr"> & { colSpan: number }) {
  return (
    <tr data-slot="table-group-row" className={cn("grp", className)} {...props}>
      <td colSpan={colSpan} className="bg-accent px-3.5 py-[7px] text-xs font-semibold text-brand-blue">
        {children}
      </td>
    </tr>
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "sticky top-0 z-(--z-sticky) border-b bg-card px-3.5 py-2.5 text-left align-middle text-xs font-medium whitespace-nowrap text-muted-foreground [&>button]:inline-flex [&>button]:items-center [&>button]:gap-1 [&>button]:font-medium [&>button_svg]:size-[13px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn("border-b border-border-subtle px-3.5 py-3 align-middle", className)}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-3 text-xs text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableGroupRow,
  TableCell,
  TableCaption,
};
